import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Button, ErrorIcon, Input, Spinner, Text, TextArea, Toggle } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { queryClient, trpc } from '../../../utils/trpc'

// ─── Types ───────────────────────────────────────────────────────────────────

type IdpType = 'azure_ad' | 'okta'

interface OktaFormValues {
	enabled: boolean
	idpType: IdpType
	idpIssuerUri: string
	idpSsoUrl: string
	idpCertificate: string
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const IDP_LABELS: Record<IdpType, string> = {
	azure_ad: 'Azure AD',
	okta: 'Okta IdP',
}

const IDP_PLACEHOLDERS: Record<IdpType, { issuerUri: string; ssoUrl: string }> = {
	azure_ad: {
		issuerUri: 'https://sts.windows.net/<tenant-id>/',
		ssoUrl: 'https://login.microsoftonline.com/<tenant-id>/saml2',
	},
	okta: {
		issuerUri: 'https://<domain>.okta.com',
		ssoUrl: 'https://<domain>.okta.com/app/<app-name>/sso/saml',
	},
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function DomainTagInput({
	value,
	onChange,
	error,
}: Readonly<{
	value: string[]
	onChange: (domains: string[]) => void
	error?: string
}>) {
	const [input, setInput] = useState('')

	const add = () => {
		const domain = input
			.trim()
			.toLowerCase()
			.replace(/^@/, '')
			.replace(/^https?:\/\//, '')
		if (domain && !value.includes(domain)) onChange([...value, domain])
		setInput('')
	}

	const remove = (domain: string) => onChange(value.filter((d) => d !== domain))

	return (
		<div>
			<Text variant='small' weight='semibold' className='mb-1 text-neutral-700'>
				Email Domains
			</Text>
			<div
				className={`flex flex-wrap gap-2 p-3 border rounded-lg bg-neutral-0 min-h-[44px] ${
					error ? 'border-error-500' : 'border-neutral-200'
				}`}>
				{value.map((domain) => (
					<span
						key={domain}
						className='inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 border border-primary-200 rounded text-primary-800 text-xs font-mono'>
						{domain}
						<button
							type='button'
							onClick={() => remove(domain)}
							className='ml-0.5 text-primary-500 hover:text-primary-800 leading-none'>
							×
						</button>
					</span>
				))}
				<input
					type='text'
					value={input}
					onChange={(e) => setInput(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === 'Enter' || e.key === ',') {
							e.preventDefault()
							add()
						}
					}}
					placeholder={value.length === 0 ? 'e.g. acme.com — press Enter to add' : 'Add another domain...'}
					className='flex-1 min-w-[160px] outline-none bg-transparent text-sm text-neutral-900 placeholder:text-neutral-400'
				/>
			</div>
			{error && (
				<Text variant='xsmall' className='text-error-600 mt-1'>
					{error}
				</Text>
			)}
			<Text variant='xsmall' className='text-neutral-400 mt-1'>
				Used to build the Okta routing rule. Press Enter or comma to add each domain. The{' '}
				<code className='font-mono'>@</code> prefix and protocol are stripped automatically.
			</Text>
		</div>
	)
}

function SpDetailRow({ label, value }: Readonly<{ label: string; value: string }>) {
	const [copied, setCopied] = useState(false)

	const copy = async () => {
		try {
			await navigator.clipboard.writeText(value)
			setCopied(true)
			setTimeout(() => setCopied(false), 1500)
		} catch {
			// clipboard unavailable — ignore
		}
	}

	return (
		<div className='py-3 border-b border-neutral-100 last:border-0'>
			<Text variant='xsmall' className='text-neutral-500 mb-1 uppercase tracking-wide'>
				{label}
			</Text>
			<div className='flex items-center gap-2'>
				<code className='flex-1 font-mono text-xs text-neutral-900 bg-neutral-50 px-2 py-1.5 rounded border border-neutral-200 break-all'>
					{value}
				</code>
				<button
					type='button'
					onClick={copy}
					className={`shrink-0 text-xs px-2 py-1.5 rounded border transition-colors ${
						copied
							? 'bg-success-50 border-success-300 text-success-700'
							: 'bg-neutral-0 border-neutral-200 text-neutral-600 hover:border-neutral-400 hover:text-neutral-900'
					}`}>
					{copied ? 'Copied' : 'Copy'}
				</button>
			</div>
		</div>
	)
}

function StatusBadge({ active, label }: Readonly<{ active: boolean; label: string }>) {
	return (
		<span
			className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
				active
					? 'bg-success-50 text-success-700 border border-success-200'
					: 'bg-neutral-100 text-neutral-500 border border-neutral-200'
			}`}>
			<span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-success-500' : 'bg-neutral-400'}`} />
			{label}
		</span>
	)
}

function ReadOnlyRow({ label, value, mono = false }: Readonly<{ label: string; value: string; mono?: boolean }>) {
	return (
		<div>
			<Text variant='xsmall' className='text-neutral-500 uppercase tracking-wide mb-0.5'>
				{label}
			</Text>
			<Text variant='small' className={`text-neutral-900 break-all ${mono ? 'font-mono text-xs' : ''}`}>
				{value}
			</Text>
		</div>
	)
}

function getErrorMessage(error: unknown): string {
	const isForbidden = (error as { data?: { code?: string } })?.data?.code === 'FORBIDDEN'
	return isForbidden ? 'Only viaPeople engineers can access Okta configuration.' : 'Failed to load SSO configuration.'
}

// ─── Main component ───────────────────────────────────────────────────────────

export const OktaTab = () => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const toast = useToast()

	const [isEditing, setIsEditing] = useState(false)
	const [emailDomains, setEmailDomains] = useState<string[]>([])
	const [domainError, setDomainError] = useState('')
	const [certFileName, setCertFileName] = useState<string | null>(null)
	const [showCertContent, setShowCertContent] = useState(false)
	const [confirmingRemove, setConfirmingRemove] = useState(false)
	const [initialized, setInitialized] = useState(false)

	const queryOptions = trpc.admin.sso.getByOrgId.queryOptions({ organizationId: organizationId ?? '' })
	const { data, isLoading, error } = useQuery({
		...queryOptions,
		enabled: !!organizationId,
	})

	const {
		control,
		handleSubmit,
		watch,
		setValue,
		reset,
		formState: { errors, isDirty },
	} = useForm<OktaFormValues>({
		defaultValues: { enabled: false, idpType: 'azure_ad', idpIssuerUri: '', idpSsoUrl: '', idpCertificate: '' },
	})

	// Populate form once data loads
	useEffect(() => {
		if (data && !initialized) {
			reset({
				enabled: data.enabled,
				idpType: data.idpType,
				idpIssuerUri: data.idpIssuerUri,
				idpSsoUrl: data.idpSsoUrl,
				idpCertificate: data.idpCertificate,
			})
			setEmailDomains(data.emailDomains)
			setInitialized(true)
		}
	}, [data, initialized, reset])

	const upsertOptions = trpc.admin.sso.upsert.mutationOptions()
	const upsertMutation = useMutation({
		...upsertOptions,
		onSuccess: () => {
			toast.showToast('SSO configuration saved')
			setIsEditing(false)
			queryClient.invalidateQueries(trpc.admin.sso.getByOrgId.queryOptions({ organizationId: organizationId ?? '' }))
		},
		onError: (err) => {
			toast.showToast(err instanceof Error ? err.message : 'Failed to save SSO configuration', 'error')
		},
	})

	const removeOptions = trpc.admin.sso.remove.mutationOptions()
	const removeMutation = useMutation({
		...removeOptions,
		onSuccess: () => {
			toast.showToast('SSO configuration removed')
			setInitialized(false)
			setIsEditing(false)
			setEmailDomains([])
			setCertFileName(null)
			setShowCertContent(false)
			reset({ enabled: false, idpType: 'azure_ad', idpIssuerUri: '', idpSsoUrl: '', idpCertificate: '' })
			setConfirmingRemove(false)
			queryClient.invalidateQueries(trpc.admin.sso.getByOrgId.queryOptions({ organizationId: organizationId ?? '' }))
		},
		onError: (err) => {
			toast.showToast(err instanceof Error ? err.message : 'Failed to remove SSO configuration', 'error')
			setConfirmingRemove(false)
		},
	})

	const onSubmit = (values: OktaFormValues) => {
		if (!organizationId) return
		if (emailDomains.length === 0) {
			setDomainError('At least one email domain is required')
			return
		}
		setDomainError('')
		upsertMutation.mutate({ organizationId, ...values, emailDomains })
	}

	// Immediate enable/disable toggle — does not require a full form submit
	const handleToggleEnabled = (newEnabled: boolean) => {
		if (!data || !organizationId) return
		upsertMutation.mutate({
			organizationId,
			enabled: newEnabled,
			idpType: data.idpType,
			idpIssuerUri: data.idpIssuerUri,
			idpSsoUrl: data.idpSsoUrl,
			idpCertificate: data.idpCertificate,
			emailDomains: data.emailDomains,
		})
	}

	const cancelEdit = () => {
		if (!data) return
		reset({
			enabled: data.enabled,
			idpType: data.idpType,
			idpIssuerUri: data.idpIssuerUri,
			idpSsoUrl: data.idpSsoUrl,
			idpCertificate: data.idpCertificate,
		})
		setEmailDomains(data.emailDomains)
		setCertFileName(null)
		setShowCertContent(false)
		setDomainError('')
		setIsEditing(false)
	}

	const handleCertFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return
		setCertFileName(file.name)
		setShowCertContent(false)
		const content = await file.text()
		setValue('idpCertificate', content, { shouldDirty: true })
	}

	const idpType = watch('idpType')
	const placeholders = IDP_PLACEHOLDERS[idpType]
	const isConfigured = !!data?.oktaIdpId
	const isSaving = upsertMutation.isPending
	const isRemoving = removeMutation.isPending
	const domainsChanged =
		JSON.stringify([...emailDomains].sort((a, b) => a.localeCompare(b))) !==
		JSON.stringify([...(data?.emailDomains ?? [])].sort((a, b) => a.localeCompare(b)))
	const hasUnsavedChanges = isDirty || domainsChanged

	if (!organizationId) return null

	// ─── Loading / error ───────────────────────────────────────────────────────

	if (isLoading) {
		return (
			<div className='p-6'>
				<Spinner />
			</div>
		)
	}

	if (error) {
		return (
			<div className='p-6'>
				<Alert message={getErrorMessage(error)} icon={ErrorIcon} className='bg-error-100 border-error-300' />
			</div>
		)
	}

	// ─── View mode (configured + not editing) ─────────────────────────────────

	if (isConfigured && !isEditing) {
		return (
			<div className='p-6 max-w-3xl mx-auto space-y-6'>
				{/* Header */}
				<div className='flex items-start justify-between'>
					<div>
						<Text variant='h3' className='mb-1'>
							Okta Configuration
						</Text>
						<Text variant='small' className='text-neutral-500'>
							SAML SSO is configured for this organization.
						</Text>
					</div>
					<Button size='small' variant='secondary' onClick={() => setIsEditing(true)}>
						Edit
					</Button>
				</div>

				{/* Summary card */}
				<div className='border border-neutral-200 rounded-lg bg-neutral-0 overflow-hidden'>
					<div className='px-5 py-4 border-b border-neutral-100 bg-neutral-50 flex items-center justify-between gap-4'>
						<div className='flex items-center gap-2 flex-wrap'>
							<StatusBadge active={!!data.oktaIdpId} label='IdP active' />
							<StatusBadge active={!!data.oktaRuleId} label='Routing rule active' />
						</div>
						<div className='flex items-center gap-3 shrink-0'>
							<Text variant='small' className='text-neutral-600'>
								{data.enabled ? 'SSO enabled' : 'SSO disabled'}
							</Text>
							<Toggle checked={data.enabled} onCheckedChange={handleToggleEnabled} label='' disabled={isSaving} />
						</div>
					</div>
					<div className='p-5 grid grid-cols-2 gap-5'>
						<ReadOnlyRow label='Identity Provider' value={IDP_LABELS[data.idpType]} />
						<ReadOnlyRow label='Email Domains' value={data.emailDomains.join(', ')} />
						<ReadOnlyRow label='Issuer URI' value={data.idpIssuerUri} mono />
						<ReadOnlyRow label='SSO URL' value={data.idpSsoUrl} mono />
						<div>
							<Text variant='xsmall' className='text-neutral-500 uppercase tracking-wide mb-0.5'>
								Certificate
							</Text>
							<Text variant='small' className='text-success-700'>
								Certificate configured
							</Text>
						</div>
					</div>
				</div>

				{/* SP Details — prominent, this is the main next action */}
				<div className='border border-primary-200 rounded-lg bg-neutral-0 overflow-hidden'>
					<div className='px-5 py-4 border-b border-primary-100 bg-primary-50'>
						<Text variant='small' weight='semibold' className='text-primary-900'>
							SP Details — send to client
						</Text>
						<Text variant='xsmall' className='text-primary-700 mt-0.5'>
							Copy these values into the client's IdP app configuration to complete the setup.
						</Text>
					</div>
					<div className='px-5 py-1'>
						{data.spDetails ? (
							<>
								<SpDetailRow label='Entity ID' value={data.spDetails.entityId} />
								<SpDetailRow label='Reply URL (ACS URL)' value={data.spDetails.acsUrl} />
								<SpDetailRow label='Audience URI' value={data.spDetails.audienceUri} />
							</>
						) : (
							<div className='py-4'>
								<Text variant='small' className='text-neutral-500'>
									SP details unavailable — the IdP may still be provisioning in Okta.
								</Text>
							</div>
						)}
					</div>
				</div>

				{/* Okta References */}
				<div className='border border-neutral-200 rounded-lg bg-neutral-0 overflow-hidden'>
					<div className='px-5 py-4 border-b border-neutral-100 bg-neutral-50'>
						<Text variant='small' weight='semibold' className='text-neutral-700'>
							Okta References
						</Text>
						<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
							Internal Okta resource IDs, useful for support and debugging.
						</Text>
					</div>
					<div className='px-5 py-1'>
						{data.oktaIdpId && <SpDetailRow label='Okta IdP ID' value={data.oktaIdpId} />}
						{data.oktaRuleId && <SpDetailRow label='Routing Rule ID' value={data.oktaRuleId} />}
					</div>
				</div>

				{/* Danger zone */}
				<div className='border border-error-200 rounded-lg overflow-hidden'>
					<div className='px-5 py-4 border-b border-error-200 bg-error-50'>
						<Text variant='small' weight='semibold' className='text-error-700'>
							Danger Zone
						</Text>
					</div>
					<div className='p-5 flex items-center justify-between gap-4'>
						<div>
							<Text variant='small' weight='semibold' className='text-neutral-900'>
								Remove SSO configuration
							</Text>
							<Text variant='xsmall' className='text-neutral-500 mt-0.5'>
								Deletes the IdP and routing rule from Okta. Users will no longer be able to use SSO.
							</Text>
						</div>
						{confirmingRemove ? (
							<div className='flex items-center gap-2 shrink-0'>
								<Text variant='xsmall' className='text-error-700 font-medium'>
									Are you sure?
								</Text>
								<Button
									size='small'
									variant='secondary'
									onClick={() => setConfirmingRemove(false)}
									disabled={isRemoving}>
									Cancel
								</Button>
								<Button
									size='small'
									variant='primary'
									onClick={() => removeMutation.mutate({ organizationId: organizationId })}
									disabled={isRemoving}
									isLoading={isRemoving}
									className='bg-error-600 hover:bg-error-700 border-error-600'>
									Yes, remove
								</Button>
							</div>
						) : (
							<Button
								size='small'
								variant='secondary'
								onClick={() => setConfirmingRemove(true)}
								className='shrink-0 border-error-300 text-error-700 hover:bg-error-50'>
								Remove
							</Button>
						)}
					</div>
				</div>
			</div>
		)
	}

	// ─── Form (new config or editing) ─────────────────────────────────────────

	return (
		<div className='p-6 max-w-2xl mx-auto'>
			{/* Header */}
			<div className='flex items-center justify-between mb-6'>
				<div>
					<Text variant='h3' className='mb-1'>
						{isEditing ? 'Edit Okta Configuration' : 'Okta Configuration'}
					</Text>
					<Text variant='small' className='text-neutral-500'>
						{isEditing
							? 'Changes are applied to Okta immediately on save.'
							: 'Configure SAML SSO for this organization.'}
					</Text>
				</div>
				{isEditing && (
					<Button size='small' variant='secondary' onClick={cancelEdit} disabled={isSaving}>
						Cancel
					</Button>
				)}
			</div>

			<form onSubmit={handleSubmit(onSubmit)}>
				<div className='border border-neutral-200 rounded-lg bg-neutral-0'>
					{/* IdP type — segmented control */}
					<div className='p-5 border-b border-neutral-100 flex flex-col items-center'>
						<Text variant='small' weight='semibold' className='mb-3 text-neutral-700'>
							Identity Provider Type
						</Text>
						<Controller
							name='idpType'
							control={control}
							render={({ field }) => (
								<div className='inline-flex rounded-lg border border-neutral-200 bg-neutral-100 p-1 gap-1'>
									{(['azure_ad', 'okta'] as IdpType[]).map((type) => (
										<button
											key={type}
											type='button'
											onClick={() => field.onChange(type)}
											className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
												field.value === type
													? 'bg-neutral-0 text-neutral-900 shadow-sm border border-neutral-200'
													: 'text-neutral-500 hover:text-neutral-700'
											}`}>
											{IDP_LABELS[type]}
										</button>
									))}
								</div>
							)}
						/>
					</div>

					{/* URLs */}
					<div className='p-5 space-y-4 border-b border-neutral-100'>
						<Controller
							name='idpIssuerUri'
							control={control}
							rules={{ required: 'IdP Issuer URI is required' }}
							render={({ field }) => (
								<Input
									label='IdP Issuer URI'
									placeholder={placeholders.issuerUri}
									value={field.value}
									onChange={field.onChange}
									variant={errors.idpIssuerUri ? 'error' : 'default'}
									hint={errors.idpIssuerUri?.message}
								/>
							)}
						/>
						<Controller
							name='idpSsoUrl'
							control={control}
							rules={{ required: 'IdP SSO URL is required' }}
							render={({ field }) => (
								<Input
									label='IdP SSO URL'
									placeholder={placeholders.ssoUrl}
									value={field.value}
									onChange={field.onChange}
									variant={errors.idpSsoUrl ? 'error' : 'default'}
									hint={errors.idpSsoUrl?.message}
								/>
							)}
						/>
					</div>

					{/* Certificate */}
					<div className='p-5 border-b border-neutral-100'>
						<div className='flex items-center justify-between mb-3'>
							<Text variant='small' weight='semibold' className='text-neutral-700'>
								Signing Certificate
							</Text>
							<div className='flex items-center gap-2'>
								<label className='inline-flex items-center justify-center gap-x-1 px-3 py-2 text-sm rounded-lg font-medium transition-colors cursor-pointer bg-neutral-0 border border-neutral-300 text-neutral-800 hover:bg-neutral-50 hover:border-neutral-500'>
									Browse file
									{/* aria-hidden keeps this out of Playwright's accessibility tree while label still triggers it */}
									<input
										type='file'
										accept='.cer,.pem,.crt,.cert'
										onChange={handleCertFile}
										aria-hidden='true'
										tabIndex={-1}
										className='absolute opacity-0 w-0 h-0 overflow-hidden'
									/>
								</label>
							</div>
						</div>
						{certFileName && (
							<div className='flex items-center justify-between p-3 border border-success-300 bg-success-50 rounded-lg mb-3'>
								<div className='flex items-center gap-2'>
									<span className='text-success-600 text-sm'>✓</span>
									<Text variant='small' className='text-success-800'>
										Certificate loaded from <span className='font-mono'>{certFileName}</span>
									</Text>
								</div>
								<button
									type='button'
									onClick={() => setShowCertContent(!showCertContent)}
									className='text-xs text-neutral-500 underline hover:text-neutral-700'>
									{showCertContent ? 'Hide' : 'View'}
								</button>
							</div>
						)}
						<Controller
							name='idpCertificate'
							control={control}
							rules={{ required: 'Signing certificate is required' }}
							render={({ field }) => (
								<div className={certFileName && !showCertContent ? 'hidden' : ''}>
									<TextArea
										placeholder={
											'Paste the Base64 certificate content here, or use Browse file above.\n\nAccepts PEM / .cer / .crt format — headers optional.'
										}
										rows={6}
										value={field.value}
										onChange={field.onChange}
										variant={errors.idpCertificate ? 'error' : 'default'}
										hint={errors.idpCertificate?.message}
										className='font-mono text-xs'
									/>
								</div>
							)}
						/>
					</div>

					{/* Domains */}
					<div className='p-5 border-b border-neutral-100'>
						<DomainTagInput
							value={emailDomains}
							onChange={(domains) => {
								setEmailDomains(domains)
								if (domains.length > 0) setDomainError('')
							}}
							error={domainError}
						/>
					</div>

					{/* SSO toggle + save */}
					<div className='p-5 flex items-center justify-between'>
						<Controller
							name='enabled'
							control={control}
							render={({ field }) => (
								<Toggle
									checked={field.value}
									onCheckedChange={field.onChange}
									label={field.value ? 'Enable SSO on save' : 'Leave SSO disabled'}
								/>
							)}
						/>
						<div className='flex items-center gap-3'>
							{isEditing && hasUnsavedChanges && (
								<Text variant='xsmall' className='text-warning-600 font-medium'>
									Unsaved changes
								</Text>
							)}
							<Button size='small' type='submit' variant='primary' disabled={isSaving} isLoading={isSaving}>
								{isConfigured ? 'Save changes' : 'Configure SSO'}
							</Button>
						</div>
					</div>
				</div>
			</form>
		</div>
	)
}
